import { Box, Divider, InputAdornment, Select as MUISelect, TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as SearchIcon } from "assets/searchicon.svg";
import { isFunction } from "lodash";
import { useEffect, useState } from "react";
import { tokens } from "styles/Tokens";
import Button from "../Button";
import Typography from "../Typography";
import Validation from "../Validation";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface Props {
  name?: string;
  register?: (val: any) => void;
  error?: any;
  validations?: any;
  label?: string;
  options?: any;
  startAdornment?: any;
  disabled?: boolean;
  showBold?: boolean;
  initialValue?: any;
  sx?: any;
  showButton?: boolean;
  buttonLabel?: string;
  startIcon?: any;
  handleChangeUpdate?: (val: any) => void;
  selectStatement?: string;
  isInputEnabled?: boolean;
  handleInputChange?: (val: string) => void;
  inputValue?: string;
  className?: string;
}

export default function Select(props: Props) {
  const {
    label,
    options,
    startAdornment,
    disabled,
    showBold,
    initialValue,
    handleChangeUpdate,
    sx,
    showButton,
    buttonLabel,
    startIcon,
    selectStatement = (
      <Typography variant="body1" color={tokens.Text.General.Tertiary}>
        Select one
      </Typography>
    ),
    register,
    name,
    error,
    validations,
    isInputEnabled = false,
    inputValue = "",
    handleInputChange,
    className = ""
  } = props;
  const [selectedOption, setSelectedOption] = useState();

  const handleChange = (event: any) => {
    const {
      target: { value }
    } = event;

    setSelectedOption(event.target.value);
    if (handleChangeUpdate) {
      handleChangeUpdate(value);
    }
  };

  const stopImmediatePropagation = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    if (initialValue) {
      setSelectedOption(initialValue);
    }
    if (isFunction(handleChangeUpdate)) handleChangeUpdate(initialValue);
  }, [initialValue]);

  return (
    <Box textAlign="left">
      {label ? (
        <>
          <Typography component="span" color={tokens.Text.Feedback.Alert}>
            {validations?.required ? " * " : ""}
          </Typography>{" "}
          <Typography
            color={disabled ? tokens.Text.General.Tertiary : tokens.Text.General.PrimaryHeadline}
            textAlign="left"
            component="span"
            variant="body2"
          >
            {label}
          </Typography>
        </>
      ) : (
        <></>
      )}

      <MUISelect
        IconComponent={ArrowDown}
        fullWidth={true}
        sx={{
          borderRadius: "8px",
          margin: "8px 0",
          height: "43px",
          display: "flex",
          color: tokens.Text.General.PrimaryBody,
          textAlign: "left",
          fontWeight: showBold ? "700" : "",
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: error ? tokens.Border.Feedback.Alert : tokens.Border.General.Tertiary
          },
          ".MuiSelect-icon": { alignSelf: "center", top: "40%" },
          ...sx
        }}
        disabled={disabled}
        value={selectedOption}
        onChange={handleChange}
        renderValue={(selected) => {
          if (!selected) {
            return initialValue || selectStatement;
          }
          return options?.find((el: any) => el.id === selected)?.val;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              "& .MuiMenuItem-root:hover": {
                backgroundColor: tokens.Background.General.Color,
                color: tokens.Text.General.Color
              },
              "& .MuiMenuItem-root.Mui-selected": {
                backgroundColor: tokens.Background.General.Primary
              },
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP
            }
          }
        }}
        displayEmpty
        inputProps={isFunction(register) ? { ...register(name, validations) } : {}}
        startAdornment={startAdornment ?? <></>}
      >
        {isInputEnabled && (
          <MenuItem
            value={""}
            key="inputItem"
            onClickCapture={stopImmediatePropagation}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end" sx={{ padding: "0px 10px" }}>
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{
                width: "100%",
                maxHeight: "43px"
              }}
              value={inputValue}
              onChange={(e: any) => handleInputChange?.(e.target.value)}
            />
          </MenuItem>
        )}
        {options?.map((option: any) => (
          <MenuItem
            sx={{
              height: "40px",
              color: selectedOption === option?.val ? tokens.Text.General.Color : "none"
            }}
            key={option?.id}
            value={option?.id}
            className={`${className}-${option?.val}`}
          >
            <Typography className={`${className}-${option?.val}`}> {option?.val}</Typography>
          </MenuItem>
        ))}

        {showButton ? (
          <Box>
            <Divider></Divider>
            <Box display={"flex"} alignItems="center" justifyContent={"center"}>
              <Button startIcon={startIcon} label={buttonLabel}></Button>
            </Box>
          </Box>
        ) : (
          <Box />
        )}
      </MUISelect>
      {error && <Validation name={name} type={error?.type} />}
    </Box>
  );
}
